import { z } from 'zod';

// ==================== PROJECT TYPES ====================
export const projectTypeEnum = z.enum([
  'fiscal',
  'audit',
  'payroll',
  'consulting',
  'legal',
  'accounting',
  'other',
]);

export type ProjectType = z.infer<typeof projectTypeEnum>;

export const projectStatusEnum = z.enum([
  'active',
  'completed',
  'paused',
  'cancelled',
]);

export type ProjectStatus = z.infer<typeof projectStatusEnum>;

// ==================== PROJECT SCHEMA ====================
export const projectSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  userId: z.string().uuid(),
  name: z.string().min(1).max(255),
  type: projectTypeEnum,
  status: projectStatusEnum,
  description: z.string().max(1000).nullable().optional(),
  startDate: z.string().nullable().optional(),
  dueDate: z.string().nullable().optional(),
  portalVisible: z.boolean().default(false),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Project = z.infer<typeof projectSchema>;

// ==================== CREATE PROJECT ====================
export const createProjectRequestSchema = z.object({
  clientId: z.string().uuid(),
  name: z.string().min(1).max(255),
  type: projectTypeEnum,
  description: z.string().max(1000).optional(),
  startDate: z.string().optional(),
  dueDate: z.string().optional(),
});

export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;

// ==================== UPDATE PROJECT ====================
export const updateProjectRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  type: projectTypeEnum.optional(),
  status: projectStatusEnum.optional(),
  description: z.string().max(1000).nullable().optional(),
  startDate: z.string().nullable().optional(),
  dueDate: z.string().nullable().optional(),
  portalVisible: z.boolean().optional(),
});

export type UpdateProjectRequest = z.infer<typeof updateProjectRequestSchema>;

// ==================== PROJECT WITH STATS ====================
export const projectWithStatsSchema = projectSchema.extend({
  folderCount: z.number().int().optional(),
  fileCount: z.number().int().optional(),
});

export type ProjectWithStats = z.infer<typeof projectWithStatsSchema>;

// ==================== PROJECT TYPE LABELS ====================
export const projectTypeLabels: Record<ProjectType, string> = {
  fiscal: 'Fiscal',
  audit: 'Auditoría',
  payroll: 'Nómina',
  consulting: 'Consultoría',
  legal: 'Legal',
  accounting: 'Contabilidad',
  other: 'Otro',
};

export const projectStatusLabels: Record<ProjectStatus, string> = {
  active: 'Activo',
  completed: 'Completado',
  paused: 'Pausado',
  cancelled: 'Cancelado',
};
